import {Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import {ElevationApp} from "../ElevationApp";
import {EventEmitter2} from "../util/EventEmitter2";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {hasDoorPrep, hasFramePrep, hasQty, isHinge, placeableOnDoor} from "../scenes/hardware/hardwarePreps";
import {HardwareItem} from "../../../../../api/Hardware";
import {isDeadboltItem} from "../../hardware/prepCanvas/PrepManager";
import {getProductCode} from "../../hardware/HardwareGroup";
import {doorPlacementViolations, HardwarePlacementViolation} from "../model/hardware/MdHardwareRules";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {useEffect, useReducer} from "react";
import {Sidebar} from "./Sidebar";
import {SidebarWarning, WarningsSection} from "./WarningsSection";
import {LiteOverlap, liteOverlaps} from "../model/hardware/MdHardwareFootprint";
import {MdDisplayView} from "../model/view/MdViewSettings";

// the hardware workspace's right sidebar: the warnings (a hardware item over a lite), then
// the opening's hardware group with every item and its active / inactive / common qtys.
// Every item is on the drawing (see MdHardware.ensureAll); the row's hint says where.
// Clicking a row selects every placement of the item, so it highlights everywhere the
// drawing renders it; the row reads selected while any of them is. Folds to a thin strip;
// the collapsed state is remembered in viewSettings.hardwareSidebarCollapsed.
export function HardwareSidebar(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const workspace = useEventEmitter2(app?.viewSettings.workspace);
    const collapsed = useEventEmitter2(app?.viewSettings.hardwareSidebarCollapsed);
    const ready = useEventEmitter2(app?.ready);
    const activeView = useEventEmitter2(app?.viewSettings.activeView);
    // re-render as placements are added/removed, as lites change, and as the canvas
    // selection changes.
    const listVersion = useEventEmitter2(app?.hardware.listChanged);
    useEventEmitter2(app?.doorSpec.litesChanged);
    const selection = useEventEmitter2(app?.viewSettings.selection);

    // re-render as placements move (drag / dimension edit) so the rule warnings track them.
    const [, forceRender] = useReducer((x: number) => x + 1, 0);
    useEffect(() => {
        if(!app) return;
        const subs = app.hardware.list.flatMap(p => [p.x.subscribe(() => forceRender()), p.y.subscribe(() => forceRender())]);
        return () => subs.forEach(sub => sub.cancel());
    }, [app, listVersion]);

    if(!app || !ready || workspace !== "hardware") return null;

    const group = app.hardwareGroup;
    const items = (group?.hardware ?? []).filter(i => !i.archived);
    const isPair = app.frameSpec.isPair;
    const deadbolt = items.find(i => isDeadboltItem(i, isPair)) ?? null;

    const placementsOf = (item: HardwareItem) => app.hardware.list.filter(p => p.productId === item.product);
    const isSelected = (item: HardwareItem) => placementsOf(item).some(p => (selection ?? []).some(s => s.id === p.id));

    // select every placement of the item (door + frame, both leaves); a second click
    // clears the selection. An item with no placement (a cosmetic one) has nothing to show.
    const select = (item: HardwareItem) => {
        const placements = placementsOf(item);
        if(placements.length === 0) return;
        if(isSelected(item)) {
            app.viewSettings.clearSelection();
            return;
        }
        app.viewSettings.selection.emit(placements.map(p => ({type: "hardware-item" as const, id: p.id, kind: p.target, productId: p.productId})));
        app.viewSettings.activeTab.emit("selected");
    };

    // select the placement over the lite and, when the other leaf's view is up, switch to
    // its leaf.
    const highlight = (o: LiteOverlap) => {
        const view: MdDisplayView = o.placement.side === "active" ? "location-active" : "location-inactive";
        if(activeView !== view) app.showView(view);
        app.viewSettings.selectItem({type: "hardware-item", id: o.placement.id, kind: o.placement.target, productId: o.placement.productId}, false);
        app.viewSettings.activeTab.emit("selected");
    };

    const fmt = app.draw.formatDimension;
    const warnings: SidebarWarning[] = liteOverlaps(app).map(o => ({
        key: `${o.placement.id}:${o.lite.id}`,
        title: `${o.item.category} overlaps ${o.lite.mark}`,
        detail: [
            getProductCode(o.item),
            isPair ? `${o.placement.side} leaf` : null,
            `${fmt(o.overlap.x1 - o.overlap.x0)} × ${fmt(o.overlap.y1 - o.overlap.y0)} over the lite`,
        ].filter(Boolean).join(" · "),
        onClick: () => highlight(o),
    }));

    return (
        <Sidebar
            name="Hardware"
            title={group ? `Hardware Group ${group.name}` : "Hardware"}
            subtitle={`Deadbolt: ${deadbolt ? deadbolt.productCode : "None"}`}
            collapsed={!!collapsed}
            onCollapsedChange={value => app.viewSettings.hardwareSidebarCollapsed.emit(value)}
        >
                <WarningsSection warnings={warnings} />
                {items.length === 0 && (
                    <Typography color="text.secondary" variant="body2" sx={{py: 2, textAlign: "center"}}>
                        {group ? "No hardware in this group." : "No hardware group on this opening."}
                    </Typography>
                )}
                {items.length > 0 && (
                    <Table size="small" stickyHeader sx={{
                        // fixed layout so the Item column shrinks to the sidebar width and
                        // its text ellipsizes instead of widening the table.
                        tableLayout: "fixed",
                        width: "100%",
                        "& td, & th": {px: 0.5, py: 0},
                        "& th": {fontSize: 11, lineHeight: 1.2, py: 0.25},
                        "& td.qty": {fontSize: 11, color: "text.secondary"},
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Active qty"><span>A</span></Tooltip></TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Inactive qty"><span>I</span></Tooltip></TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Common qty"><span>C</span></Tooltip></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(item => (
                                <HardwareRow
                                    key={item.id}
                                    item={item}
                                    isPair={isPair}
                                    selected={isSelected(item)}
                                    selectable={placementsOf(item).length > 0}
                                    violations={placementsOf(item)
                                        .flatMap(p => doorPlacementViolations(app, p, item, app.draw.formatDimension))}
                                    onSelect={() => select(item)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
        </Sidebar>
    );
}

function HardwareRow(props: {item: HardwareItem; isPair: boolean; selected: boolean; selectable: boolean; violations: HardwarePlacementViolation[]; onSelect: () => void}) {
    const item = props.item;
    const hinge = isHinge(item);
    const door = placeableOnDoor(item);
    const frame = hasFramePrep(item);

    // for a pair, note which leaf(s) the door prep lands on. A single opening only has
    // the active leaf, so the label would be noise.
    const hasActive = door && hasQty(item, true);
    const hasInactive = door && hasQty(item, false);
    const leaf = door && props.isPair
        ? (hasActive && hasInactive ? "Both leaves" : hasActive ? "Active leaf" : hasInactive ? "Inactive leaf" : null)
        : null;

    // where the item is on the drawing; hinges are placed by the Hinges tools.
    const prep = hinge ? "Hinges are always shown; edit them with the Hinges tools"
        : door && frame ? "Door + Frame"
        : door && !hasDoorPrep(item) ? "Door (surface mounted, no prep)"
        : door ? "Door"
        : frame ? "Frame"
        : "No prep";
    const hint = [prep, leaf].filter(Boolean).join(" · ");
    const sku = getProductCode(item);

    return (
        <TableRow hover title={hint} selected={props.selected} sx={{cursor: props.selectable ? "pointer" : "default"}} onClick={() => props.selectable && props.onSelect()}>
            <TableCell sx={{overflow: "hidden"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5, minWidth: 0}}>
                    <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>{item.category}</Typography>
                    {props.violations.length > 0 && (
                        <Tooltip title={<>{props.violations.map((v, i) => <div key={i}>{v.category}: {v.message}</div>)}</>}>
                            <WarningAmberIcon color="error" sx={{fontSize: 16}} />
                        </Tooltip>
                    )}
                    {isDeadboltItem(item, props.isPair) && <Chip size="small" color="secondary" variant="outlined" label="Deadbolt" sx={{height: 18, fontSize: 10}} />}
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap component="div">{sku}</Typography>
            </TableCell>
            {item.productIsAutoQtyHinge ? (
                <>
                    <TableCell className="qty" align="right"><AutoQty override={item.overrideHingeActiveQty} /></TableCell>
                    <TableCell className="qty" align="right"><AutoQty override={item.overrideHingeInActiveQty} /></TableCell>
                    <TableCell className="qty" align="right">-</TableCell>
                </>
            ) : (
                <>
                    <TableCell className="qty" align="right">{item.activeQty}</TableCell>
                    <TableCell className="qty" align="right">{item.inactiveQty}</TableCell>
                    <TableCell className="qty" align="right">{item.commonQty}</TableCell>
                </>
            )}
        </TableRow>
    );
}

// an auto-qty hinge's leaf qty: "*" for auto (by door height, as the hardware group editor
// marks it), or the override when one is set.
function AutoQty(props: {override: number | null}) {
    if(props.override !== null && props.override !== undefined) {
        return <Tooltip title={`Auto calculated, overridden to ${props.override}`}><span>{props.override}</span></Tooltip>;
    }
    return <Tooltip title="Auto calculated"><span>*</span></Tooltip>;
}
